<script setup>
import { onMounted, ref, watch, nextTick, computed } from "vue";
import { useAnalyticsStore } from "@/store/useAnalyticsStore";
import Chart from "chart.js/auto";

const chartRef = ref(null);
const store = useAnalyticsStore();
const isLoading = ref(true);
let chartInstance = null;

const labels = ["San José", "Alajuela", "Cartago", "Heredia", "Guanacaste", "Puntarenas", "Limón", "Extranjeros"];
const provincias = { "San José": "1", Alajuela: "2", Cartago: "3", Heredia: "4", Guanacaste: "5", Puntarenas: "6", Limón: "7", Extranjeros: "8" };

// Revisa si los datos están listos
const isDataReady = computed(() => store.generalData && Object.keys(store.generalData).length > 0);

// Formatea los datos para el gráfico
const formatDataForChart = () => {

  const positivos = [], negativos = [], neutros = [], noIdentificados = [];

  labels.forEach((label) => {
    const key = provincias[label];
    if (key && store.generalData[key]) {
      const provinceData = store.generalData[key];     

      positivos.push(provinceData.positivo || 0);
      negativos.push(provinceData.negativo || 0);
      neutros.push(provinceData.neutro || 0);
      noIdentificados.push(provinceData.no_identificado || 0);
    } else {
      positivos.push(0);
      negativos.push(0);
      neutros.push(0);
      noIdentificados.push(0);
    }
  });

  return { positivos, negativos, neutros, noIdentificados };

};

// Render del gráfico
const renderChart = async () => {
  if (!chartRef.value || !isDataReady.value) {
    console.warn("Chart not rendered: No data or canvas missing.");
    isLoading.value = false;
    return;
  }

  await nextTick(); // Asegura que el canvas esté renderizado

  const ctx = chartRef.value.getContext("2d");

  // Destruye la instancia anterior del gráfico
  if (chartInstance) {
    chartInstance.destroy();
  }

  const { positivos, negativos, neutros, noIdentificados } = formatDataForChart();

  chartInstance = new Chart(ctx, {
    type: "bar",
    data: {
      labels,
      datasets: [
        { label: "Positivo", backgroundColor: "#2dce89", data: positivos, stack: "votos" },
        { label: "Negativo", backgroundColor: "#FF4767", data: negativos, stack: "votos" },
        { label: "Neutro", backgroundColor: "#344767", data: neutros, stack: "votos" },
        { label: "No Identificado", backgroundColor: "#c3cfdb", data: noIdentificados, stack: "votos" },
      ],
    },
    options: {
      indexAxis: "x",
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: true } },
      scales: {
        y: {
          stacked: true,
          grid: { drawBorder: false, display: true, drawOnChartArea: true, borderDash: [5, 5] },
          ticks: { display: true, padding: 10, color: "#9ca2b7" },
          title: { display: true, text: "Votantes", color: "#9ca2b7" },
        },
        x: {
          stacked: true,
          grid: { drawBorder: false, display: false, drawOnChartArea: true, drawTicks: true },
          ticks: { display: true, color: "#9ca2b7", padding: 10 },
        },
      },
    },
  });

  isLoading.value = false;
};


onMounted(async () => {
  isLoading.value = true;

  try {
    const response = await store.fetchData(); 
    
  } catch (error) {
    console.error("Error fetching data:", error);
  }

  await renderChart();

  // Fallback de seguridad para evitar que el spinner se quede cargando
  setTimeout(() => {
    isLoading.value = false;
  }, 5000);
});

// Revisa el store para renderizar el gráfico
watch(
  () => store.generalData,
  async (newData) => {
    if (newData) {
      await renderChart();
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="card h-100">
    <div class="p-3 card-header">
      <div class="d-flex justify-content-between">
        <h6 class="mb-0">Distribución por Estado</h6>
        <div v-if="isLoading" class="spinner-border text-primary" role="status"></div>
      </div>
    </div>
    <div class="p-3 card-body">
      <div class="chart" v-if="!isLoading">
        <canvas ref="chartRef" class="chart-canvas" height="300"></canvas>
      </div>
      <p v-if="!isLoading && !isDataReady" class="text-center text-muted">No hay datos disponibles</p>
    </div>
  </div>
</template>
